import { useEffect, useMemo, useState } from "react";

import CustomGrid from "../CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";

import { Box, Paper } from "@mui/material";

import { GridRowSelectionModel } from "@mui/x-data-grid";

import AddContainerModal from "./AddContainerModal";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { EnhancedTableToolbar } from "../EnhancedToolbar";

import { Container } from "types";
import { CONTAINERS_URL as BASE_URL } from "constants/urls";

import { ENTITY } from "constants/entities";
import EditContainerModal from "./EditContainerModal";
import Loader from "../Loader";
import useModals from "hooks/useModals";
import useCrud from "hooks/useCrud";
import { STATUS_CODES } from "constants/index";
import { useSession } from "providers/SessionProvider";
import { useToast } from "hooks/useToast";
import { useFilter } from "hooks/useFilter";
import { getUserFullName } from "helpers";
import { exportToExcel } from "helpers/exportToExcel";

export default function ContainersList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const {
    containers,
    addSessionData,
    updateSessionData,
    deleteSessionData,
    users,
  } = useSession();

  const { showSnackBar } = useToast();

  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();

  const {
    loading,
    firstLoad,
    createData: createContainer,
    updateData: updateContainer,
    deleteData: deleteContainer,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<Container>(BASE_URL);

  const enrichedContainers = useMemo(() => {
    return containers.map((item) => ({
      ...item,
      created_by_name: item.created_by
        ? getUserFullName(item.created_by, users)
        : "SISTEMA",
    }));
  }, [containers, users]);

  const {
    filterText,
    setFilterText,
    filteredData: filteredContainers,
  } = useFilter<Container & { created_by_name: string }>({
    data: enrichedContainers,
    fields: gridStructure[ENTITY.CONTAINERS].fields,
  });

  const containersExcelColumns = {
    Descripción: (c: Container) => c.description ?? "-",
    "M3 mínimo": (c: Container) => c.m3_min ?? "-",
    "M3 máximo": (c: Container) => c.m3_max ?? "-",
    "Peso Volumétrico": (c: Container) => c.volume_weight ?? "-",
    Activo: (c: Container) => (c.is_active ? "Sí" : "No"),
    "Creado por": (c: Container & { created_by_name?: string }) =>
      c.created_by_name,
  } satisfies Record<string, (row: Container) => any>;

  const handleExportContainersExcel = () => {
    exportToExcel<Container>({
      data: filteredContainers,
      columns: containersExcelColumns,
      sheetName: "Contenedores",
      fileNamePrefix: "Contenedores",
      headerRows: [
        ["Listado de Contenedores"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  const handleDeleteSelected = async () => {
    const failedDeletes: number[] = [];

    for (const id of selected) {
      const reponse = await deleteContainer(id, true, true);
      if (reponse.status !== STATUS_CODES.OK) {
        failedDeletes.push(Number(id));
      }
    }
    deleteSessionData(
      ENTITY.CONTAINERS,
      selected
        .filter((item) => !failedDeletes.includes(Number(item)))
        .map((item) => item.toString()),
    );

    setSelected([]);
  };

  const handleEdit = async (containerToEdit: Container) => {
    const response = await updateContainer(
      containerToEdit.id,
      containerToEdit,
      true,
      true,
    );
    if (response.status === STATUS_CODES.OK) {
      updateSessionData(ENTITY.CONTAINERS, containerToEdit);
    }

    setSelected([]);
  };

  const handleCreate = async (newContainer: Container) => {
    const response = await createContainer(newContainer, true, true);
    if (response.status === STATUS_CODES.OK) {
      addSessionData(ENTITY.CONTAINERS, response.data as Container);
    }
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      {firstLoad ? (
        <Loader isFirstLoad={firstLoad} isLoading={loading} />
      ) : (
        <>
          <Loader isFirstLoad={firstLoad} isLoading={loading} />
          <AddContainerModal
            open={addModalOpen}
            handleOpen={() => setAddModalOpen(!addModalOpen)}
            onCreate={handleCreate}
          />
          <DeleteConfirmationModaL
            open={deleteModalOpen}
            handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            entityName={ENTITY.CONTAINERS}
            values={selected.map((item) => {
              const value = containers.find((c) => c.id === item)?.description;
              if (value) return value;
              return "";
            })}
            handleDelete={handleDeleteSelected}
          />
          {selected.length > 0 ? (
            <EditContainerModal
              open={editModalOpen}
              handleOpen={() => setEditModalOpen(!editModalOpen)}
              value={containers.find((c) => c.id === selected[0])}
              onEdit={handleEdit}
            />
          ) : null}
          <Box sx={{ width: "99%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
                handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
                handleDeleteModalOpen={() =>
                  setDeleteModalOpen(!deleteModalOpen)
                }
                onFilterChange={setFilterText}
                onExportExcel={handleExportContainersExcel}
              />
              <CustomGrid<Container>
                structure={gridStructure[ENTITY.CONTAINERS]}
                rows={filteredContainers}
                onEdit={handleEdit}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
    </>
  );
}
